/**
 * Durée marketing d'une formation ou d'un programme.
 *
 * Historiquement, Product.duration est un texte libre saisi en français
 * (« 100 heures ») et affiché brut, y compris sur les pages anglaises.
 * Le champ numérique Product.durationHours prend le relais quand il est
 * renseigné ; tant qu'il est vide, on parse la valeur historique pour la
 * reformater dans la langue du visiteur, et on retombe sur la chaîne brute
 * si elle n'est pas parsable (aucune régression possible).
 */

const RAW_HOURS_PATTERN = /^(\d+)\s*(?:heures?|hours?|h)$/i;

/**
 * Nombre d'heures exploitable, ou null si indéterminable.
 * `course` accepte une formation backend, un programme ou une entrée de
 * lib/courses-catalog.js.
 */
export function getCourseDurationHours(course) {
    if (!course) return null;

    const hours = Number(course.durationHours);
    if (Number.isInteger(hours) && hours > 0) return hours;

    const raw = typeof course.duration === "string" ? course.duration.trim() : "";
    const match = raw.match(RAW_HOURS_PATTERN);
    return match ? parseInt(match[1], 10) : null;
}

/**
 * Libellé localisé de la durée (« 100 heures » / « 100 hours »).
 * `t` est un translator next-intl sans namespace (useTranslations() côté
 * client, await getTranslations() côté serveur). Retourne la chaîne brute
 * si la durée n'est pas parsable, "" si aucune durée n'existe.
 */
export function formatCourseDuration(course, t) {
    const hours = getCourseDurationHours(course);
    if (hours !== null) return t("course_duration_hours", { hours });
    return (course && course.duration) || "";
}

/**
 * Durée au format ISO 8601 pour le JSON-LD schema.org (« PT100H »),
 * ou undefined si indéterminable (la clé est alors omise du JSON-LD,
 * ce qui est préférable à une valeur invalide).
 */
export function courseDurationToISO(course) {
    const hours = getCourseDurationHours(course);
    return hours !== null ? `PT${hours}H` : undefined;
}
